import React, { useCallback, useEffect, useMemo, useRef } from 'react';

type VisibilityCallback = (state: { isInView: boolean }) => void;

interface Config {
  isAbsolute?: boolean;
  threshold?: number;
}

/**
 * React hook that provides a `<span>` sentinel and monitors its visibility via Intersection Observer.
 *
 * The hook internally creates a single `<span>` element (returned to the caller) and
 * attaches an `IntersectionObserver`. Whenever the sentinel enters or leaves the
 * viewport, the supplied `callback` is invoked with the latest visibility state.
 *
 * @param callback – Function executed on every visibility change.
 *   Receives `{ isInView: boolean }` where `true` means the span is currently inside the viewport.
 * @param config  – Optional settings.
 *   • `isAbsolute` (default `false`) – if `true`, the span is rendered with `position: absolute`;
 *     otherwise it is `position: relative`.
 *
 * @returns A memoised React `<span>` element.
 * Place this element anywhere in your component tree to start observing.
 *
 * @example
 * ```tsx
 * const sentinel = useInViewObserver(({ isInView }) => {
 *   console.info('Sentinel visible?', isInView);
 * });
 *
 * return (
 *   <section>
 *     {sentinel}
 *   </section>
 * );
 * ```
 */
const useInViewObserver = (
  callback: VisibilityCallback,
  config: Config = { isAbsolute: false, threshold: 0 }
) => {
  const targetRef = useRef<HTMLSpanElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const observerCallback = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      callback({ isInView: entry.isIntersecting });
    },
    [callback]
  );

  useEffect(() => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(observerCallback, {
        root: null,
        threshold: config.threshold,
      });
    }

    const { current: currentTarget } = targetRef;
    const { current: currentObserver } = observerRef;

    if (currentTarget) {
      currentObserver.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        currentObserver.unobserve(currentTarget);
      }
    };
  }, [observerCallback, config]);

  const targetSpan = useMemo(
    () =>
      React.createElement('span', {
        ref: targetRef,
        style: {
          position: config.isAbsolute ? 'absolute' : 'relative',
          height: '1rem',
          left: 0,
        },
      }),
    [config]
  );

  return targetSpan;
};

// eslint-disable-next-line import/prefer-default-export
export { useInViewObserver };
